import { useEffect, useState } from "react";
import {
  ChevronLeft,
  ChevronRight,
  Pause,
  Play,
  RotateCcw,
  X,
} from "lucide-react";
import { act, seed, type Action, type DemoState } from "../domain/demo";
export const tourScenes: {
  page: string;
  time: string;
  actor: string;
  title: string;
  story: string;
  actions: Action[];
}[] = [
  {
    page: "overview",
    time: "07:00",
    actor: "Maya Chen · Lab Manager",
    title: "A real working day, three customer commitments",
    story:
      "Northstar needs six diesel results by 16:00. Meridian and Harbor work continue in parallel.",
    actions: [],
  },
  {
    page: "schedule",
    time: "07:10",
    actor: "Maya Chen · Lab Manager",
    title: "Check the next 24 hours",
    story:
      "Review day, evening and night coverage. Nitrogen reserve and night-shift D93 coverage need attention.",
    actions: [],
  },
  {
    page: "samples",
    time: "07:35–10:58",
    actor: "Aisha Patel · Analyst",
    title: "Samples move through the laboratory",
    story:
      "Six D86 runs are completed in sequence. Sample, order, instrument and original result stay linked.",
    actions: [],
  },
  {
    page: "detection",
    time: "11:12",
    actor: "Instrument monitor · simulated",
    title: "Instrument behavior flags possible result bias",
    story:
      "DIST-04 heating response is abnormal. Next routine QC is at 13:00; this is an early warning, not a failed QC result.",
    actions: [],
  },
  {
    page: "investigation",
    time: "11:18",
    actor: "Maya Chen · Lab Manager",
    title: "Contain the affected work",
    story:
      "Maya approves the result hold. Six samples are blocked from customer release; other orders continue.",
    actions: ["hold"],
  },
  {
    page: "investigation",
    time: "11:50",
    actor: "Luis Ortega · Senior Analyst",
    title: "Test competing explanations",
    story:
      "Inspection records a seating gap. The reference thermometer check does not support probe drift. Original evidence is retained.",
    actions: ["investigate", "conclude"],
  },
  {
    page: "capa",
    time: "12:00",
    actor: "Maya Chen · Independent approver",
    title: "Review the cause, then approve the plan",
    story:
      "Maya approves Luis’s conclusion. The plan assigns corrective and preventive work, with a separate 30-day effectiveness check.",
    actions: ["sign", "plan", "approve"],
  },
  {
    page: "schedule",
    time: "12:15–13:10",
    actor: "Luis Ortega + Aisha Patel",
    title: "Carry out approved work",
    story:
      "Reseat the flask, verify the reference and revise the setup check. Retained aliquots are rerun on DIST-02.",
    actions: ["correct"],
  },
  {
    page: "capa",
    time: "15:10",
    actor: "Maya Chen · Lab Manager",
    title: "Review recovery QC and retests",
    story:
      "Passing control results, the alternate instrument and six retest values are recorded before release.",
    actions: ["verify"],
  },
  {
    page: "samples",
    time: "15:20",
    actor: "Maya Chen · Report authorizer",
    title: "Authorize customer results",
    story:
      "The approved retests replace the suspect values for reporting. Original values remain in the technical record.",
    actions: ["release"],
  },
  {
    page: "export",
    time: "15:22",
    actor: "Maya Chen · Lab Manager",
    title: "Prepare the authorized LIMS handoff",
    story:
      "CSV, JSON or XML may be downloaded. This demo does not send a message or claim that the customer has received results.",
    actions: [],
  },
  {
    page: "binder",
    time: "15:25",
    actor: "Quality reviewer",
    title: "Open the evidence behind every decision",
    story:
      "Inspect calibration, QC, sample lineage, approvals and the report excerpt. The 30-day effectiveness section correctly remains pending.",
    actions: [],
  },
];
export function sceneState(lab: string, index: number) {
  let s = seed(lab);
  for (const scene of tourScenes.slice(0, index + 1))
    for (const a of scene.actions) s = act(s, a);
  return s;
}
export default function Walkthrough({
  lab,
  onScene,
  onClose,
}: {
  lab: string;
  onScene: (s: DemoState, page: string) => void;
  onClose: () => void;
}) {
  const [index, setIndex] = useState(0);
  const [playing, setPlaying] = useState(true);
  const [speed, setSpeed] = useState(6);
  const scene = tourScenes[index];
  useEffect(() => {
    onScene(sceneState(lab, index), scene.page);
  }, [index, lab]);
  useEffect(() => {
    if (!playing || index === tourScenes.length - 1) return;
    const t = setTimeout(() => setIndex((i) => i + 1), speed * 1000);
    return () => clearTimeout(t);
  }, [index, playing, speed]);
  return (
    <section className="tour-player" aria-label="Animated lab walkthrough">
      <div className="tour-progress" key={index + "-" + playing + "-" + speed}>
        <span
          style={{ width: ((index + 1) / tourScenes.length) * 100 + "%" }}
        />
      </div>
      <div className="tour-main">
        <div className="tour-avatar">
          {scene.actor
            .split(" ")
            .slice(0, 2)
            .map((w) => w[0])
            .join("")}
        </div>
        <div className="tour-narration" key={index}>
          <small>
            SCENE {index + 1}/{tourScenes.length} · {scene.time} · {scene.actor}
          </small>
          <strong>{scene.title}</strong>
          <p>{scene.story}</p>
        </div>
        <div className="tour-controls">
          <button
            className="icon-button"
            aria-label="Previous scene"
            disabled={index === 0}
            onClick={() => {
              setPlaying(false);
              setIndex((i) => i - 1);
            }}
          >
            <ChevronLeft size={19} />
          </button>
          <button
            className="button primary"
            aria-label={playing ? "Pause walkthrough" : "Play walkthrough"}
            onClick={() => setPlaying(!playing)}
          >
            {playing ? <Pause size={15} /> : <Play size={15} />}
          </button>
          <button
            className="icon-button"
            aria-label="Next scene"
            disabled={index === tourScenes.length - 1}
            onClick={() => {
              setPlaying(false);
              setIndex((i) => i + 1);
            }}
          >
            <ChevronRight size={19} />
          </button>
          <select
            aria-label="Walkthrough speed"
            value={speed}
            onChange={(e) => setSpeed(Number(e.target.value))}
          >
            <option value={8}>Slow</option>
            <option value={6}>Normal</option>
            <option value={3}>Fast</option>
          </select>
          <button
            className="icon-button"
            aria-label="Restart walkthrough"
            onClick={() => {
              setIndex(0);
              setPlaying(true);
            }}
          >
            <RotateCcw size={15} />
          </button>
          <button className="button" onClick={onClose}>
            <X size={14} /> Exit
          </button>
        </div>
      </div>
      <div className="tour-chapters">
        {tourScenes.map((x, i) => (
          <button
            key={i}
            className={i === index ? "active" : ""}
            aria-label={"Go to scene " + (i + 1)}
            onClick={() => {
              setPlaying(false);
              setIndex(i);
            }}
          >
            {i + 1}
          </button>
        ))}
        <small>
          Isolated replay · your saved lab session is restored on exit
        </small>
      </div>
    </section>
  );
}
